/**
 * vCard Utility Functions
 * Generates vCard 3.0 format for contact saving
 */

// Format phone numbers for vCard
const formatPhoneForVCard = (contact) => {
  const { type, value, country } = contact;
  
  if (type === 'phone' || type === 'whatsapp') {
    const prefix = country === 'SG' ? '+65' : '+91';
    return `${prefix}${value}`;
  }
  return value;
};

// Get vCard phone type
const getVCardPhoneType = (contactType) => {
  switch (contactType) {
    case 'phone':
      return 'VOICE';
    case 'whatsapp':
      return 'VOICE'; // WhatsApp numbers are still voice-capable
    default:
      return 'VOICE';
  }
};

// Escape special characters for vCard format
const escapeVCardValue = (value) => {
  if (!value) return '';
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
};

// Generate vCard content
export const generateVCard = (card) => {
  const {
    name = '',
    designation = '',
    company_name = '',
    address = '',
    contacts = [],
    websites = [],
    social_profiles = [],
    profile_picture_url = '',
    company_logo_url = ''
  } = card;

  // Build vCard content
  let vcard = 'BEGIN:VCARD\n';
  vcard += 'VERSION:3.0\n';
  
  // Name
  const escapedName = escapeVCardValue(name);
  vcard += `FN:${escapedName}\n`;
  vcard += `N:${escapedName};;;;\n`;
  
  // Title/Designation
  if (designation) {
    vcard += `TITLE:${escapeVCardValue(designation)}\n`;
  }
  
  // Organization
  if (company_name) {
    vcard += `ORG:${escapeVCardValue(company_name)}\n`;
  }
  
  // Address
  if (address) {
    const escapedAddress = escapeVCardValue(address);
    vcard += `ADR:;;${escapedAddress};;;;\n`;
  }
  
  // Contact information
  contacts.forEach((contact) => {
    if (contact.type === 'email') {
      vcard += `EMAIL;TYPE=INTERNET:${escapeVCardValue(contact.value)}\n`;
    } else if (contact.type === 'phone') {
      const phoneNumber = formatPhoneForVCard(contact);
      vcard += `TEL;TYPE=${getVCardPhoneType(contact.type)}:${phoneNumber}\n`;
    } else if (contact.type === 'whatsapp') {
      const phoneNumber = formatPhoneForVCard(contact);
      // Add as both phone and custom WhatsApp field
      vcard += `TEL;TYPE=VOICE:${phoneNumber}\n`;
      vcard += `X-WHATSAPP:${phoneNumber}\n`;
    }
  });
  
  // Websites
  if (websites && websites.length > 0) {
    websites.forEach((website) => {
      if (website.url) {
        vcard += `URL:${escapeVCardValue(website.url)}\n`;
      }
    });
  }
  
  // Social profiles as URLs
  if (social_profiles && social_profiles.length > 0) {
    social_profiles.forEach((profile) => {
      if (profile.url) {
        vcard += `URL:${escapeVCardValue(profile.url)}\n`;
        // Add platform-specific custom fields
        switch (profile.platform.toLowerCase()) {
          case 'linkedin':
            vcard += `X-LINKEDIN:${escapeVCardValue(profile.url)}\n`;
            break;
          case 'twitter':
            vcard += `X-TWITTER:${escapeVCardValue(profile.url)}\n`;
            break;
          case 'instagram':
            vcard += `X-INSTAGRAM:${escapeVCardValue(profile.url)}\n`;
            break;
          case 'facebook':
            vcard += `X-FACEBOOK:${escapeVCardValue(profile.url)}\n`;
            break;
        }
      }
    });
  }
  
  // Profile picture
  if (profile_picture_url) {
    vcard += `PHOTO;VALUE=URI:${escapeVCardValue(profile_picture_url)}\n`;
  }
  
  // Note with company info
  if (company_name || designation) {
    let note = '';
    if (designation && company_name) {
      note = `${designation} at ${company_name}`;
    } else if (designation) {
      note = designation;
    } else if (company_name) {
      note = company_name;
    }
    if (note) {
      vcard += `NOTE:${escapeVCardValue(note)}\n`;
    }
  }
  
  vcard += 'END:VCARD';
  
  return vcard;
};

// Download vCard file
export const downloadVCard = (card) => {
  try {
    const vCardContent = generateVCard(card);
    const blob = new Blob([vCardContent], { type: 'text/vcard;charset=utf-8' });
    
    // Create filename from name
    const filename = `${(card.name || 'contact').replace(/[^a-zA-Z0-9]/g, '_')}.vcf`;
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading vCard:', error);
    return false;
  }
};

// Save to contacts (for mobile devices)
export const saveToContacts = (card) => {
  // Try native contact saving first (mobile devices)
  if (navigator.contacts && navigator.contacts.create) {
    try {
      const contact = navigator.contacts.create();
      
      // Set basic info
      contact.displayName = card.name || 'Contact';
      contact.name = {
        formatted: card.name || 'Contact',
        familyName: '',
        givenName: card.name || 'Contact'
      };
      
      // Add phone numbers
      if (card.contacts) {
        const phoneNumbers = card.contacts
          .filter(c => c.type === 'phone' || c.type === 'whatsapp')
          .map(c => ({
            type: c.type === 'whatsapp' ? 'mobile' : 'mobile',
            value: formatPhoneForVCard(c),
            pref: false
          }));
        
        if (phoneNumbers.length > 0) {
          contact.phoneNumbers = phoneNumbers;
        }
        
        // Add emails
        const emails = card.contacts
          .filter(c => c.type === 'email')
          .map(c => ({
            type: 'work',
            value: c.value,
            pref: false
          }));
        
        if (emails.length > 0) {
          contact.emails = emails;
        }
      }
      
      // Add organization
      if (card.company_name) {
        contact.organizations = [{
          type: 'work',
          name: card.company_name,
          department: '',
          title: card.designation || ''
        }];
      }
      
      // Save contact
      contact.save();
      return true;
    } catch (error) {
      console.error('Native contact saving failed:', error);
    }
  }
  
  // Fallback to vCard download
  return downloadVCard(card);
};

// Main function to save contact
export const saveContactToDevice = (card) => {
  try {
    // Validate card data
    if (!card) {
      console.error('No card data provided');
      return false;
    }

    if (!card.name && !card.contacts?.length) {
      console.error('Card must have at least a name or contact information');
      return false;
    }

    // Check if it's mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Try native contact saving first, fallback to vCard
      return saveToContacts(card);
    } else {
      // Desktop - use vCard download
      return downloadVCard(card);
    }
  } catch (error) {
    console.error('Error saving contact:', error);
    return false;
  }
}; 